import React from "react";
import { translateService, dateHelperService } from "../../services/imports";
import VerifiedIdentity from "./verifiedIdentity";

const t = (k, ...a) => translateService.t(`signings.${k}`, ...a);
const date = (ms) => (ms ? dateHelperService.format(ms, dateHelperService.smallDateTimeFormat) : "");

// who, as the caller reads it: "You", "through your link" (whoever held the
// link sent to the caller signed as them), else the sign-in name or address
const who = (entry) => {
  if (entry.byCaller) return t(entry.throughLink ? "throughYourLink" : "you");
  const by = entry.signedBy || {};
  return by.name || by.email || "";
};

// how the signer had access, beside the name, with what it means as a
// tooltip: the share's link, the share signed in, or no share at all - the
// document's workspace, its owner or a member, which /signings does not
// tell apart. "through your link" already says it.
const how = (entry) => {
  const email = (entry.signedBy && entry.signedBy.email) || "";
  if (entry.throughLink)
    return entry.byCaller ? null : { text: t("throughLink", email), hint: t("accessHint.link") };
  return entry.shareId != null
    ? { text: t("throughShare", email), hint: t("accessHint.share") }
    : { text: t("member"), hint: t("accessHint.workspace") };
};

// "Who signed" (vsys docs/signings-runbook.md §4): the signings stored
// through validate.ge - not the file's signatures, which the panel below
// lists - each with who, how, when, the required-signatory badge and the
// identity signature-gateway verified. The process id is never shown.
// Where the reader may see the summary, its counts head the list - counts
// only (owner, 2026-10-18): who signed, and as whom, is the rows', since one
// account may sign twice and be verified as two people.
//
// props: signings (the /signings list; null while loading), error (it could
// not be read), summary (/signings/summary, only where the reader may see it)
export default class SigningsSection extends React.Component {
  renderBadge(entry) {
    return entry.requiredSignatoryMet ? <span className="signings__badge">{t("required")}</span> : null;
  }

  // "Signatures: 2 · Verified people: 2 · Accounts: 1", the verified people
  // left out when none was verified; the required signatories when any share
  // requires one
  renderCounts(summary) {
    const required = summary.requiredSignatories || {};
    const signings = summary.signings || 0;
    const accounts = summary.accounts || 0;
    const verified = summary.verifiedSignatories || 0;
    return (
      <p className="signings__counts">
        <span>{verified ? t("counts", signings, verified, accounts) : t("countsNoVerified", signings, accounts)}</span>
        {required.total > 0 && <span>{t("requiredCount", required.signed || 0, required.total)}</span>}
      </p>
    );
  }

  renderSigning = (s) => {
    const way = how(s);
    return (
      <li key={s.processId} className="signings__row">
        <div className="signings__line">
          <span className="signings__who">{who(s)}</span>
          {way && (
            <span className="signings__how" title={way.hint}>
              <span className="signings__label">{t("access")}</span> <span className="signings__access">{way.text}</span>
            </span>
          )}
          {this.renderBadge(s)}
        </div>
        <span className="signings__date">{date(s.signedAt)}</span>
        <VerifiedIdentity value={s.verifiedSignatory} />
      </li>
    );
  };

  render() {
    const { signings, error, summary } = this.props;
    return (
      <section className={`signings ${translateService.key}`} aria-labelledby="signings-title">
        <h3 id="signings-title" className="signings__title">
          {t("title")}
        </h3>
        <p className="signings__hint">{t("hint")}</p>
        {summary && this.renderCounts(summary)}
        {error ? (
          <p className="signings__error" role="alert">
            {t("loadError")}
          </p>
        ) : !signings ? (
          <img src="./assets/images/black-loading.svg" className="content-loader" alt="" />
        ) : signings.length === 0 ? (
          <p className="signings__none">{t("none")}</p>
        ) : (
          <ul className="signings__list">{signings.map(this.renderSigning)}</ul>
        )}
      </section>
    );
  }
}
